import React, { useEffect, useState, useContext } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store/store';
import NetworkService from '../../../config/interceptor/interceptor';
import { SnackbarContext } from '../../../config/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { AppDispatch } from '../../../redux/store/store';
import { useDispatch } from 'react-redux';
import { setUserData } from '../../../redux/store/userData-slices';

type UpdateProfileData = {
  fullName: string;
  phoneNumber: string;
  email: string;
  governorate: string;
  birthday: string;
  educationLevel: string;
};

type ChangePasswordData = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const ProfilePage = () => {
  const userData = useSelector((state: RootState) => state.user.userData);
  // Helper to check if user is admin, teacher, or super teacher
  const isAdminOrTeacher = userData && ["ROLE_ADMIN", "ROLE_TEACHER", "ROLE_SUPER_TEACHER"].includes(userData.role?.name);
  const snackbarContext = useContext(SnackbarContext);
  const navigation = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const role = userData?.role?.name;
   const userInitials = userData
    ? userData.fullName
        .split(' ')
        .map(n => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase()
    : '';
  // État pour gérer l'onglet actif
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');
  
  const [profileForm, setProfileForm] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    governorate: '',
    birthday: '',
    educationLevel: '',
  });
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [profileErrors, setProfileErrors] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    governorate: '',
    birthday: '',
    educationLevel: '',
  });
  
  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  
  const [isLoading, setIsLoading] = useState({
    profile: false,
    password: false
  });

  useEffect(() => {
    if (userData) {
      setProfileForm(prev => ({
        ...prev,
        fullName: userData.fullName,
        phoneNumber: userData.phoneNumber,
        email: userData.email,
        governorate: userData.governorate,
        birthday: userData.birthday.split('T')[0],
        educationLevel: isAdminOrTeacher ? '' : (userData as any).educationLevel || ''
      }));
    }
  }, [userData, isAdminOrTeacher]);

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    validateProfileField(name, value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    validatePasswordField(name, value);
  };

  const validateProfileField = (name: string, value: string) => {
    let error = '';
    switch (name) {
      case "email":
        if (!value) {
          error = "Email est requis.";
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          error = "Email invalide.";
        }
        break;
      case "fullName":
        if (!value) {
          error = "Le nom complet est requis.";
        }
        break;
      case "phoneNumber":
        if (!value) {
          error = "Le numéro de téléphone est requis.";
        } else if (!/^[0-9]{8}$/.test(value)) {
          error = "Le numéro de téléphone doit contenir exactement 8 chiffres.";
        }
        break;
      case "governorate":
        if (!value) {
          error = "Le gouvernorat est requis.";
        }
        break;
      case "birthday":
        if (!value) {
          error = "La date de naissance est requise.";
        }
        break;
      case "educationLevel":
        if (isAdminOrTeacher) {
          error = '';
        } else if (!value) {
          error = "Le niveau d'éducation est requis.";
        }
        break;
      default:
        break;
    }
    setProfileErrors(prev => ({ ...prev, [name]: error }));
    return error;
  };

  const validatePasswordField = (name: string, value: string) => {
    let error = '';
    
    switch (name) {
      case "currentPassword":
        if (!value) {
          error = "L'ancien mot de passe est requis.";
        }
        break;
      
      case "newPassword":
        if (!value) {
          error = "Le nouveau mot de passe est requis.";
        } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>\/+\\[\]\-])[A-Za-z\d!@#$%^&*(),.?":{}|<>\/+\\[\]\-]{8,}$/.test(value)) {
          error = "Le mot de passe doit comporter au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial.";
        }
        break;
        
      case "confirmPassword":
        if (!value) {
          error = "Veuillez confirmer le nouveau mot de passe.";
        } else if (value !== passwordForm.newPassword) {
          error = "Les mots de passe ne correspondent pas.";
        }
        break;
        
      default:
        break;
    }
    
    setPasswordErrors(prev => ({ ...prev, [name]: error }));
    return error;
  };

  const validateProfileForm = () => {
    let isValid = true;
    const newErrors = {...profileErrors};
    (Object.keys(profileForm) as (keyof typeof profileForm)[]).forEach(key => {
      if (key === 'educationLevel' && isAdminOrTeacher) return;
      const error = validateProfileField(key, profileForm[key]);
      if (error) {
        isValid = false;
        newErrors[key] = error;
      }
    });
    setProfileErrors(newErrors);
    return isValid;
  };

  const validatePasswordForm = () => {
    let isValid = true;
    const newErrors = {...passwordErrors};

    (Object.keys(passwordForm) as (keyof typeof passwordForm)[]).forEach(key => {
      const error = validatePasswordField(key, passwordForm[key]);
      if (error) {
        isValid = false;
        newErrors[key] = error;
      }
    });

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = "Les mots de passe ne correspondent pas.";
      isValid = false;
    }

    setPasswordErrors(newErrors);
    return isValid;
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateProfileForm()) {
      snackbarContext?.showMessage("Erreur", "Veuillez corriger les erreurs dans le formulaire", "error");
      return;
    }

    setIsLoading(prev => ({ ...prev, profile: true }));
    try {
      const dataToSend: UpdateProfileData = {
        fullName: profileForm.fullName,
        phoneNumber: profileForm.phoneNumber,
        email: profileForm.email,
        governorate: profileForm.governorate,
        birthday: profileForm.birthday,
        educationLevel: isAdminOrTeacher ? undefined : profileForm.educationLevel,
      } as any;
      const response = await updateProfileService(dataToSend);

      snackbarContext?.showMessage("Succès", "Profil mis à jour avec succès", "success");
      
      if (userData) {
        dispatch(setUserData({
          ...userData,
          ...dataToSend
        }));
      }

      setTimeout(() => {
        navigation('/home');
      }, 1500);
      
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour:', error);
      
      let errorMessage = "Échec de la mise à jour du profil. Veuillez réessayer.";
      if (error.response && error.response.data) {
        errorMessage = error.response.data.message || errorMessage;
      }
      
      snackbarContext?.showMessage("Erreur", errorMessage, "error");
    } finally {
      setIsLoading(prev => ({ ...prev, profile: false }));
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) {
      snackbarContext?.showMessage("Erreur", "Veuillez corriger les erreurs dans le formulaire de mot de passe", "error");
      return;
    }

    setIsLoading(prev => ({ ...prev, password: true }));
    
    try {
      const dataToSend: ChangePasswordData = {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword
      };

      const response = await changePasswordService(dataToSend);

      snackbarContext?.showMessage("Succès", "Mot de passe mis à jour avec succès", "success");
      
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
    } catch (error: any) {
      console.error('Erreur lors du changement de mot de passe:', error);
      
      let errorMessage = "Échec de la mise à jour du mot de passe. Veuillez réessayer.";
      if (error.response && error.response.data) {
        errorMessage = error.response.data.message || errorMessage;
      }
      
      snackbarContext?.showMessage("Erreur", errorMessage, "error");
    } finally {
      setIsLoading(prev => ({ ...prev, password: false }));
    }
  };

  if (!userData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto border-t-2 border-b-2 border-blue-500 rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">Chargement des données du profil...</p>
        </div>
      </div>
    );
  }

  const governorates = [
    'ARIANA', 'BEJA', 'BEN_AROUS', 'BIZERTE', 'GABES', 'GAFSA', 'JENDOUBA',
    'KAIRUAN', 'KASSERINE', 'KEBILI', 'KEF', 'MAHDIA', 'MANOUBA', 'MEDENINE',
    'MONASTIR', 'NABEUL', 'SFAX', 'SIDI_BOUZID', 'SILIANA', 'SOUSSE', 'TATAOUINE',
    'TOZEUR', 'TUNIS', 'ZAGHOUAN'
  ];

  const educationLevels = [
    { value: 'PERMIER_ANNEE_PRIMAIRE', label: 'Première année primaire' },
    { value: 'DEUXIEME_ANNEE_PRIMAIRE', label: 'Deuxième année primaire' },
    { value: 'TROISIEME_ANNEE_PRIMAIRE', label: 'Troisième année primaire' },
    { value: 'QUATRIEME_ANNEE_PRIMAIRE', label: 'Quatrième année primaire' },
    { value: 'CINQUIEME_ANNEE_PRIMAIRE', label: 'Cinquième année primaire' },
    { value: 'SIXIEME_ANNEE_PRIMAIRE', label: 'Sixième année primaire' },
    { value: 'SEPTIEME_ANNEE_DE_BASE', label: 'Septième année de base' },
    { value: 'HUITIEME_ANNEE_DE_BASE', label: 'Huitième année de base' },
    { value: 'NEUVIEME_ANNEE_DE_BASE', label: 'Neuvième année de base' },
    { value: 'PREMIERE_ANNEE_SECONDAIRE', label: 'Première année secondaire' },
    { value: 'DEUXIEME_ANNEE_SECONDAIRE_ECONOMIE_GESTION', label: 'Deuxième année secondaire - Économie & Gestion' },
    { value: 'DEUXIEME_ANNEE_SECONDAIRE_INFORMATIQUE', label: 'Deuxième année secondaire - Informatique' },
    { value: 'DEUXIEME_ANNEE_SECONDAIRE_LETTRES', label: 'Deuxième année secondaire - Lettres' },
    { value: 'DEUXIEME_ANNEE_SECONDAIRE_SCIENCES', label: 'Deuxième année secondaire - Sciences' },
    { value: 'TROISIEME_ANNEE_SECONDAIRE_ECONOMIE', label: 'Troisième année secondaire - Économie' },
    { value: 'TROISIEME_ANNEE_SECONDAIRE_INFORMATIQUE', label: 'Troisième année secondaire - Informatique' },
    { value: 'TROISIEME_ANNEE_SECONDAIRE_LETTRES', label: 'Troisième année secondaire - Lettres' },
    { value: 'TROISIEME_ANNEE_SECONDAIRE_SCIENCES', label: 'Troisième année secondaire - Sciences' },
    { value: 'TROISIEME_ANNEE_SECONDAIRE_MATH', label: 'Troisième année secondaire - Mathématiques' },
    { value: 'TROISIEME_ANNEE_SECONDAIRE_TECHNIQUE', label: 'Troisième année secondaire - Technique' },
    { value: 'BAC_LETTRES', label: 'Bac Lettres' },
    { value: 'BAC_SCIENCES', label: 'Bac Sciences' },
    { value: 'BAC_MATH', label: 'Bac Mathématiques' },
    { value: 'BAC_INFORMATIQUE', label: 'Bac Informatique' },
    { value: 'BAC_ECONOMIE_GESTION', label: 'Bac Économie & Gestion' },
    { value: 'FORMATION_PROFESSIONNELLE', label: 'Formation Professionnelle' },
  ];

  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <div className="max-w-6xl p-4 mx-auto md:p-8 animate-in fade-in duration-700">
      <div className="overflow-hidden bg-white/80 backdrop-blur-xl border border-white shadow-[0_30px_70px_-15px_rgba(0,0,0,0.1)] rounded-[3rem]">
        {/* State-of-the-art Header */}
        <div className="relative px-8 py-12 md:px-12 bg-gradient-to-br from-[#09745F] via-[#00B69B] to-[#048C6B] overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 blur-[80px] rounded-full translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-black/5 blur-[50px] rounded-full -translate-x-1/4 translate-y-1/4"></div>
          
          <div className="relative z-10 flex flex-col items-center gap-8 md:flex-row md:gap-10 text-center md:text-left">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-tr from-white/40 to-white/10 rounded-full blur opacity-75 group-hover:opacity-100 transition duration-500"></div>
              <div className="relative bg-white/20 backdrop-blur-md border-2 border-white/30 rounded-full w-28 h-28 flex items-center justify-center text-3xl font-black text-white shadow-2xl transition-transform duration-500 group-hover:scale-110">
                {userInitials}
              </div>
            </div>
            
            <div className="flex-1">
              <h1 className="text-3xl font-black text-white uppercase tracking-tighter md:text-4xl">Mon Espace</h1>
              <p className="mt-2 text-white/80 font-medium text-lg max-w-lg leading-relaxed">Personnalisez votre expérience et gérez votre sécurité en quelques clics.</p>
            </div>
            
            <div className="px-6 py-3 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
              <span className="text-[10px] font-black text-white/50 uppercase tracking-[0.3em]">Statut du compte</span>
              <p className="text-sm font-black text-white uppercase tracking-widest mt-1">{role?.replace('ROLE_', '')}</p>
            </div>
          </div>
        </div>
        
        <div className="flex flex-col md:flex-row min-h-[600px]">
          {/* Colonne gauche - Modern Sidebar Menu */}
          <div className="w-full border-r border-gray-100 md:w-80 bg-gray-50/50 p-6 md:p-8">
            <div className="space-y-3">
              <button
                className={`flex items-center gap-4 px-6 py-5 w-full text-left transition-all duration-300 rounded-3xl group ${
                  activeTab === 'profile' 
                    ? 'bg-[#09745f] text-white shadow-[0_15px_30px_-5px_rgba(9,116,95,0.3)] scale-[1.02]' 
                    : 'text-gray-500 hover:bg-white hover:text-[#09745f] hover:shadow-lg hover:shadow-gray-200/50'
                }`}
                onClick={() => setActiveTab('profile')}
              >
                <div className={`p-2.5 rounded-2xl transition-colors duration-300 ${activeTab === 'profile' ? 'bg-white/20 text-white' : 'bg-gray-100 group-hover:bg-[#09745f]/5 text-gray-400 group-hover:text-[#09745f]'}`}>
                  <PersonOutlineOutlinedIcon fontSize="small" />
                </div>
                <span className="font-black uppercase text-[11px] tracking-widest">Informations</span>
              </button>
              
              <button
                className={`flex items-center gap-4 px-6 py-5 w-full text-left transition-all duration-300 rounded-3xl group ${
                  activeTab === 'password' 
                    ? 'bg-[#09745f] text-white shadow-[0_15px_30px_-5px_rgba(9,116,95,0.3)] scale-[1.02]' 
                    : 'text-gray-500 hover:bg-white hover:text-[#09745f] hover:shadow-lg hover:shadow-gray-200/50'
                }`}
                onClick={() => setActiveTab('password')}
              >
                <div className={`p-2.5 rounded-2xl transition-colors duration-300 ${activeTab === 'password' ? 'bg-white/20 text-white' : 'bg-gray-100 group-hover:bg-[#09745f]/5 text-gray-400 group-hover:text-[#09745f]'}`}>
                  <LockOutlinedIcon fontSize="small" />
                </div>
                <span className="font-black uppercase text-[11px] tracking-widest">Sécurité</span>
              </button>
            </div>
            
            <div className="mt-12 p-8 bg-gradient-to-br from-gray-900 to-gray-800 rounded-[2.5rem] relative overflow-hidden hidden md:block">
               <div className="absolute top-0 right-0 w-32 h-32 bg-primary/20 blur-[40px] rounded-full translate-x-1/2 -translate-y-1/2"></div>
               <div className="relative z-10">
                  <p className="text-[10px] font-black text-white/40 uppercase tracking-[0.3em] mb-3">Support Elite</p>
                  <p className="text-sm font-bold text-white leading-relaxed">Besoin d'aide pour vos informations ? Notre équipe est disponible 24/7.</p>
                  <button 
                    onClick={() => navigation('/dashboard/support')}
                    className="mt-6 px-4 py-2 bg-white/10 hover:bg-white text-white hover:text-gray-900 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all"
                  >
                    Consulter le support
                  </button>
               </div>
            </div>
          </div>
          
          {/* Colonne droite - High Fidelity Form Content */}
          <div className="w-full p-8 md:p-16 md:w-3/4">
            {activeTab === 'profile' ? (
              <div className="animate-in slide-in-from-right duration-500">
                <div className="flex items-center gap-6 mb-12">
                   <div className="w-16 h-16 rounded-2.5xl bg-primary/10 flex items-center justify-center text-primary border border-primary/10 shadow-inner">
                      <PersonOutlineOutlinedIcon fontSize="large" />
                   </div>
                   <div>
                      <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tighter">Votre Profil</h2>
                      <p className="text-gray-400 text-sm font-medium mt-1">Maintenez vos données à jour pour un meilleur suivi.</p>
                   </div>
                </div>
                
                <form onSubmit={handleProfileSubmit} className="space-y-10">
                  <div className="grid grid-cols-1 gap-x-10 gap-y-10 md:grid-cols-2">
                    <div className="space-y-2 group">
                      <label className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Nom complet</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none transition-colors group-focus-within:text-primary">
                          <PersonOutlineOutlinedIcon fontSize="small" className="text-gray-300 group-focus-within:text-primary" />
                        </div>
                        <input
                          type="text"
                          name="fullName"
                          value={profileForm.fullName}
                          onChange={handleProfileChange}
                          className={`w-full pl-14 pr-6 py-5 bg-gray-50/50 border-2 ${profileErrors.fullName ? 'border-red-500' : 'border-gray-100'} rounded-3xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary focus:bg-white transition-all duration-300 font-bold text-gray-700 shadow-sm`}
                          placeholder="Ex: Ahmed Foulen"
                        />
                        {profileErrors.fullName && <p className="absolute -bottom-6 left-2 text-[10px] text-red-500 font-bold uppercase tracking-wider">{profileErrors.fullName}</p>}
                      </div>
                    </div>

                    <div className="space-y-2 group">
                      <label className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Téléphone</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none transition-colors group-focus-within:text-primary">
                          <PhoneIphoneOutlinedIcon fontSize="small" className="text-gray-300 group-focus-within:text-primary" />
                        </div>
                        <input
                          type="tel"
                          name="phoneNumber"
                          value={profileForm.phoneNumber}
                          onChange={handleProfileChange}
                          className={`w-full pl-14 pr-6 py-5 bg-gray-50/50 border-2 ${profileErrors.phoneNumber ? 'border-red-500' : 'border-gray-100'} rounded-3xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary focus:bg-white transition-all duration-300 font-bold text-gray-700 shadow-sm`}
                          placeholder="Número de téléphone"
                          maxLength={8}
                        />
                        {profileErrors.phoneNumber && <p className="absolute -bottom-6 left-2 text-[10px] text-red-500 font-bold uppercase tracking-wider">{profileErrors.phoneNumber}</p>}
                      </div>
                    </div>

                    <div className="space-y-2 group">
                      <label className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Email</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none transition-colors group-focus-within:text-primary">
                          <MailOutlineOutlinedIcon fontSize="small" className="text-gray-300 group-focus-within:text-primary" />
                        </div>
                        <input
                          type="email"
                          name="email"
                          value={profileForm.email}
                          onChange={handleProfileChange}
                          className={`w-full pl-14 pr-6 py-5 bg-gray-50/50 border-2 ${profileErrors.email ? 'border-red-500' : 'border-gray-100'} rounded-3xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary focus:bg-white transition-all duration-300 font-bold text-gray-700 shadow-sm`}
                          placeholder="nom@exemple.com"
                        />
                        {profileErrors.email && <p className="absolute -bottom-6 left-2 text-[10px] text-red-500 font-bold uppercase tracking-wider">{profileErrors.email}</p>}
                      </div>
                    </div>

                    <div className="space-y-2 group">
                      <label className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Gouvernorat</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none transition-colors group-focus-within:text-primary">
                          <LocationOnOutlinedIcon fontSize="small" className="text-gray-300 group-focus-within:text-primary" />
                        </div>
                        <select
                          name="governorate"
                          value={profileForm.governorate}
                          onChange={handleProfileChange}
                          className={`w-full pl-14 pr-12 py-5 bg-gray-50/50 border-2 ${profileErrors.governorate ? 'border-red-500' : 'border-gray-100'} rounded-3xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary focus:bg-white transition-all duration-300 font-bold text-gray-700 shadow-sm appearance-none`}
                        >
                          <option value="">Sélectionnez un gouvernorat</option>
                          {governorates.map(gov => (
                            <option key={gov} value={gov}>{gov.replace(/_/g, ' ')}</option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 pr-5 flex items-center pointer-events-none text-gray-400">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                        {profileErrors.governorate && <p className="absolute -bottom-6 left-2 text-[10px] text-red-500 font-bold uppercase tracking-wider">{profileErrors.governorate}</p>}
                      </div>
                    </div>

                    <div className="space-y-2 group">
                      <label className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Date de naissance</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none transition-colors group-focus-within:text-primary">
                          <CakeOutlinedIcon fontSize="small" className="text-gray-300 group-focus-within:text-primary" />
                        </div>
                        <input
                          type="date"
                          name="birthday"
                          value={profileForm.birthday}
                          onChange={handleProfileChange}
                          className={`w-full pl-14 pr-6 py-5 bg-gray-50/50 border-2 ${profileErrors.birthday ? 'border-red-500' : 'border-gray-100'} rounded-3xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary focus:bg-white transition-all duration-300 font-bold text-gray-700 shadow-sm`}
                        />
                        {profileErrors.birthday && <p className="absolute -bottom-6 left-2 text-[10px] text-red-500 font-bold uppercase tracking-wider">{profileErrors.birthday}</p>}
                      </div>
                    </div>

                    {!isAdminOrTeacher && (
                      <div className="space-y-2 group">
                        <label className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Niveau d'éducation</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none transition-colors group-focus-within:text-primary">
                            <SchoolOutlinedIcon fontSize="small" className="text-gray-300 group-focus-within:text-primary" />
                          </div>
                          <select
                            name="educationLevel"
                            value={profileForm.educationLevel}
                            onChange={handleProfileChange}
                            className={`w-full pl-14 pr-12 py-5 bg-gray-50/50 border-2 ${profileErrors.educationLevel ? 'border-red-500' : 'border-gray-100'} rounded-3xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary focus:bg-white transition-all duration-300 font-bold text-gray-700 shadow-sm appearance-none`}
                          >
                            <option value="">Niveau d'étude</option>
                            {educationLevels.map(level => (
                              <option key={level.value} value={level.value}>{level.label}</option>
                            ))}
                          </select>
                          <div className="absolute inset-y-0 right-0 pr-5 flex items-center pointer-events-none text-gray-400">
                             <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                          </div>
                          {profileErrors.educationLevel && <p className="absolute -bottom-6 left-2 text-[10px] text-red-500 font-bold uppercase tracking-wider">{profileErrors.educationLevel}</p>}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-6 sm:flex-row pt-10">
                    <button
                      type="button"
                      onClick={() => navigation('/home')}
                      className="flex-1 px-10 py-5 bg-gray-100 text-gray-400 font-black uppercase text-[11px] tracking-[0.3em] rounded-3xl hover:bg-gray-200 transition-all duration-300"
                    >
                      Annuler
                    </button>
                    
                    <button
                      type="submit"
                      disabled={isLoading.profile}
                      className={`flex-1 px-10 py-5 bg-gradient-to-r from-primary to-[#07b98e] text-white font-black uppercase text-[11px] tracking-[0.3em] rounded-3xl shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-3 ${
                        isLoading.profile ? 'opacity-50 cursor-not-allowed scale-[0.98]' : ''
                      }`}
                    >
                      {isLoading.profile ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                          Enregistrement...
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Mettre à jour
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="animate-in slide-in-from-right duration-500">
                <div className="flex items-center gap-6 mb-12">
                   <div className="w-16 h-16 rounded-2.5xl bg-red-50 flex items-center justify-center text-red-500 border border-red-100 shadow-inner">
                      <LockOutlinedIcon fontSize="large" />
                   </div>
                   <div>
                      <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tighter">Sécurité</h2>
                      <p className="text-gray-400 text-sm font-medium mt-1">Protégez votre compte avec un mot de passe robuste.</p>
                   </div>
                </div>
                
                <form onSubmit={handlePasswordSubmit} className="space-y-10">
                  <div className="space-y-10 max-w-xl mx-auto md:mx-0">
                    <div className="space-y-2 group">
                      <label className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Ancien mot de passe</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none transition-colors group-focus-within:text-primary">
                          <LockOutlinedIcon fontSize="small" className="text-gray-300 group-focus-within:text-primary" />
                        </div>
                        <input
                          type={showPasswords.current ? "text" : "password"}
                          name="currentPassword"
                          value={passwordForm.currentPassword}
                          onChange={handlePasswordChange}
                          className={`w-full pl-14 pr-16 py-5 bg-gray-50/50 border-2 ${passwordErrors.currentPassword ? 'border-red-500' : 'border-gray-100'} rounded-3xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary focus:bg-white transition-all duration-300 font-bold text-gray-700 shadow-sm`}
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-5 flex items-center text-gray-300 hover:text-primary transition-colors"
                          onClick={() => togglePasswordVisibility('current')}
                        >
                          {showPasswords.current ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </button>
                        {passwordErrors.currentPassword && <p className="absolute -bottom-6 left-2 text-[10px] text-red-500 font-bold uppercase tracking-wider">{passwordErrors.currentPassword}</p>}
                      </div>
                    </div>

                    <div className="space-y-2 group">
                      <label className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Nouveau mot de passe</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none transition-colors group-focus-within:text-primary">
                          <LockOutlinedIcon fontSize="small" className="text-gray-300 group-focus-within:text-primary" />
                        </div>
                        <input
                          type={showPasswords.new ? "text" : "password"}
                          name="newPassword"
                          value={passwordForm.newPassword}
                          onChange={handlePasswordChange}
                          className={`w-full pl-14 pr-16 py-5 bg-gray-50/50 border-2 ${passwordErrors.newPassword ? 'border-red-500' : 'border-gray-100'} rounded-3xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary focus:bg-white transition-all duration-300 font-bold text-gray-700 shadow-sm`}
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-5 flex items-center text-gray-300 hover:text-primary transition-colors"
                          onClick={() => togglePasswordVisibility('new')}
                        >
                          {showPasswords.new ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </button>
                        {passwordErrors.newPassword && <p className="absolute -bottom-6 left-2 text-[10px] text-red-500 font-bold uppercase tracking-wider leading-tight">{passwordErrors.newPassword}</p>}
                      </div>
                    </div>

                    <div className="space-y-2 group">
                      <label className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Confirmer mot de passe</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none transition-colors group-focus-within:text-primary">
                          <LockOutlinedIcon fontSize="small" className="text-gray-300 group-focus-within:text-primary" />
                        </div>
                        <input
                          type={showPasswords.confirm ? "text" : "password"}
                          name="confirmPassword"
                          value={passwordForm.confirmPassword}
                          onChange={handlePasswordChange}
                          className={`w-full pl-14 pr-16 py-5 bg-gray-50/50 border-2 ${passwordErrors.confirmPassword ? 'border-red-500' : 'border-gray-100'} rounded-3xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary focus:bg-white transition-all duration-300 font-bold text-gray-700 shadow-sm`}
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-5 flex items-center text-gray-300 hover:text-primary transition-colors"
                          onClick={() => togglePasswordVisibility('confirm')}
                        >
                          {showPasswords.confirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </button>
                        {passwordErrors.confirmPassword && <p className="absolute -bottom-6 left-2 text-[10px] text-red-500 font-bold uppercase tracking-wider">{passwordErrors.confirmPassword}</p>}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-6 sm:flex-row pt-10">
                    <button
                      type="button"
                      onClick={() => setActiveTab('profile')}
                      className="flex-1 px-10 py-5 bg-gray-100 text-gray-400 font-black uppercase text-[11px] tracking-[0.3em] rounded-3xl hover:bg-gray-200 transition-all duration-300"
                    >
                      Retour aux informations
                    </button>
                    
                    <button
                      type="submit"
                      disabled={isLoading.password}
                      className={`flex-1 px-10 py-5 bg-gradient-to-r from-red-500 to-rose-600 text-white font-black uppercase text-[11px] tracking-[0.3em] rounded-3xl shadow-lg shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40 hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-3 ${
                        isLoading.password ? 'opacity-50 cursor-not-allowed scale-[0.98]' : ''
                      }`}
                    >
                      {isLoading.password ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                          Enregistrement...
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                          </svg>
                          Sauvegarder
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const updateProfileService = async (userData: UpdateProfileData) => {
  try {
    const response = await NetworkService.getInstance().sendHttpRequest({
      url: 'users/profile',
      method: 'PUT',
      data: userData,
      withLoader: true,
      withFailureLogs: true
    });
    return response.data;
  } catch (error) {
    console.error('Erreur dans updateProfileService:', error);
    throw error;
  }
};

export const changePasswordService = async (passwordData: ChangePasswordData) => {
  try {
    const response = await NetworkService.getInstance().sendHttpRequest({
      url: 'users/change-password',
      method: 'PUT',
      data: passwordData,
      withLoader: true,
      withFailureLogs: true
    });
    return response.data;
  } catch (error) {
    console.error('Erreur dans changePasswordService:', error);
    throw error;
  }
};

export default ProfilePage;